import type { PreparedFeature } from './feature-preparation.js';
import type { PreparationService } from './preparation-protocol.js';
import { SWISS_PROJECTION } from './swiss-projection.js';
import { LruCache } from '../../shared/lru-cache.js';
import type { GeoBounds } from '../../types/terrain-types.js';

export const VECTOR_TILE_FEATURE_CACHE_ENTRIES = 2000;

/** Fetches the raw MVT bytes for one {layer}/{z}/{x}/{y} tile (possibly sharded). */
export type VectorTileRequester = (
  layer: string,
  z: number,
  x: number,
  y: number,
) => Promise<Blob[]>;

interface PendingTile {
  promise: Promise<PreparedFeature[]>;
  controller: AbortController;
  consumers: number;
  settled: boolean;
}

/**
 * Caches raw MVT bytes; decoding and geometry preparation run in workers.
 *
 * Tiles are cached per {layer}/{z}/{x}/{y} rather than composited like the
 * WMTS imagery: features from several tiles are just concatenated instead of
 * stitched into a single texture, so there is nothing to reference-count.
 */
export class VectorTileFeatureProvider {
  private readonly cache = new LruCache<string, ArrayBuffer[]>(VECTOR_TILE_FEATURE_CACHE_ENTRIES);
  private readonly inflight = new Map<string, PendingTile>();

  constructor(
    private readonly preparation: PreparationService,
    private readonly origin: () => [number, number, number],
  ) {}

  /**
   * Load every feature from the vector tiles covering `bounds` at `zoom`,
   * keyed by `{zoom}/{x}/{y}` so callers can cache/build geometry once per
   * underlying vector tile instead of once per (much finer-grained) terrain
   * tile that happens to overlap it.
   */
  async loadFeaturesForBounds(
    layerId: string,
    requestTile: VectorTileRequester,
    bounds: GeoBounds,
    zoom: number,
    signal?: AbortSignal,
  ): Promise<Map<string, PreparedFeature[]>> {
    signal?.throwIfAborted();
    const sw = SWISS_PROJECTION.lonLatToWebMercatorTile(bounds.west, bounds.south, zoom);
    const ne = SWISS_PROJECTION.lonLatToWebMercatorTile(bounds.east, bounds.north, zoom);
    const minX = Math.min(sw.x, ne.x);
    const maxX = Math.max(sw.x, ne.x);
    const minY = Math.min(sw.y, ne.y);
    const maxY = Math.max(sw.y, ne.y);

    const tileKeys: string[] = [];
    const requests: Promise<PreparedFeature[]>[] = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        tileKeys.push(`${zoom}/${x}/${y}`);
        requests.push(this.loadSingleTile(layerId, requestTile, zoom, x, y, signal));
      }
    }

    const results = await Promise.all(requests);
    signal?.throwIfAborted();
    const byTile = new Map<string, PreparedFeature[]>();
    for (let i = 0; i < tileKeys.length; i++) {
      byTile.set(tileKeys[i], results[i]);
    }
    return byTile;
  }

  clear(): void {
    this.cache.clear();
    this.abortPending();
  }

  abortPending(): void {
    for (const entry of this.inflight.values()) entry.controller.abort();
    this.inflight.clear();
  }

  private async loadSingleTile(
    layerId: string,
    requestTile: VectorTileRequester,
    z: number,
    x: number,
    y: number,
    signal?: AbortSignal,
  ): Promise<PreparedFeature[]> {
    signal?.throwIfAborted();
    const key = `${layerId}/${z}/${x}/${y}`;

    let pending = this.inflight.get(key);
    if (!pending) {
      const controller = new AbortController();
      pending = {
        promise: this.fetchAndDecode(layerId, requestTile, z, x, y, controller.signal),
        controller,
        consumers: 0,
        settled: false,
      };
      const stored = pending;
      this.inflight.set(key, stored);
      const cleanup = (): void => {
        stored.settled = true;
        if (this.inflight.get(key) === stored) {
          this.inflight.delete(key);
        }
      };
      void stored.promise.then(cleanup, cleanup);
    }
    const entry = pending;
    entry.consumers++;
    return new Promise((resolve, reject) => {
      let finished = false;
      const release = (): boolean => {
        if (finished) return false;
        finished = true;
        signal?.removeEventListener('abort', abort);
        entry.controller.signal.removeEventListener('abort', abort);
        entry.consumers--;
        if (!entry.consumers && !entry.settled) {
          entry.controller.abort();
          if (this.inflight.get(key) === entry) this.inflight.delete(key);
        }
        return true;
      };
      const abort = (): void => {
        if (release()) reject(new DOMException('Aborted', 'AbortError'));
      };
      signal?.addEventListener('abort', abort, { once: true });
      entry.controller.signal.addEventListener('abort', abort, { once: true });
      void entry.promise.then(
        features => {
          if (release()) resolve(features);
        },
        error => {
          if (release()) reject(error);
        },
      );
      if (signal?.aborted || entry.controller.signal.aborted) abort();
    });
  }

  private async fetchAndDecode(
    layerId: string,
    requestTile: VectorTileRequester,
    z: number,
    x: number,
    y: number,
    signal: AbortSignal,
  ): Promise<PreparedFeature[]> {
    const key = `${layerId}/${z}/${x}/${y}`;
    let buffers = this.cache.get(key);
    if (!buffers) {
      const blobs = await requestTile(layerId, z, x, y);
      signal.throwIfAborted();
      buffers = await Promise.all(blobs.map(blob => blob.arrayBuffer()));
      signal.throwIfAborted();
      this.cache.set(key, buffers);
    }
    return this.preparation.vector(
      {
        kind: 'vector',
        buffers,
        layerId,
        z,
        x,
        y,
        origin: this.origin(),
      },
      signal,
    );
  }
}
